import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import { ClipboardList, CloudLightning, Bell, Download, ChevronRight, CalendarDays, Clock, MapPin } from 'lucide-react';
import { Sheet, Seg, plural } from '../components/AdminUi';

// Postavke (samo admin, vrijede za cijeli DVD): izbornik sa sažetkom stanja; dodir otvara panel
// s vlastitim Odustani / Spremi (PUT /settings šalje samo promijenjene ključeve).
// E-mail obavijesti nisu u funkciji (Gmail SMTP blokiran na Hetzneru) — njihove postavke nisu prikazane.

// Zadane vrijednosti kao na backendu (getSetting / sendPushNotification) kad ključ nije spremljen.
const DEFAULTS = {
  task_count_mode: 'assigned',
  reminder_days_enabled: 'true', reminder_days: '2', reminder_days_hour: '17',
  reminder_hours_enabled: 'false', reminder_hours: '1',
  dhmz_enabled: 'false', dhmz_region: 'Grad Zagreb', dhmz_min_severity: 'Yellow',
  dhmz_push_enabled: 'true', dhmz_push_min_severity: 'Yellow',
};
const PUSH_GROUPS = [
  { label: 'Zadaci', items: [
    ['push_new_task', 'Dodjela na zadatak', 'Korisniku kad ga admin ili vlasnik grupe doda na zadatak'],
    ['push_task_removed', 'Uklonjen sa zadatka'],
    ['push_group_task', 'Novi grupni zadatak', 'Članovima grupe koji nisu izravno dodijeljeni'],
    ['push_notify_groups_allowed', 'Poruka članovima grupa', 'Dopušta „Pošalji obavijest” članovima grupa zadatka'],
    ['push_notify_assigned_allowed', 'Poruka dodijeljenima', 'Dopušta „Pošalji obavijest” dodijeljenima'],
    ['push_task_done', 'Završen / novi ponavljajući'],
    ['push_task_closed', 'Ručno zatvoren'],
    ['push_task_review', 'Prijedlog odobren / odbijen'],
    ['push_removal_request', 'Odjava prihvaćena / odbijena'],
    ['push_volunteer_task', 'Nova prijava za zadatak', 'Adminu i vlasnicima grupe, najviše jednom u 24 h po zadatku'],
  ] },
  { label: 'Intervencije', items: [
    ['push_new_intervention', 'Nova intervencija', 'Svim korisnicima'],
    ['push_intervention_participant', 'Dodijeljen na intervenciju'],
    ['push_volunteer_intervention', 'Nova prijava za intervenciju'],
  ] },
  { label: 'Ostalo', items: [
    ['push_password_reset', 'Reset lozinke'],
  ] },
];
const PUSH_KEYS = PUSH_GROUPS.flatMap(g => g.items.map(i => i[0]));
const defaultOf = (k) => (k in DEFAULTS ? DEFAULTS[k] : k.startsWith('push_') ? 'true' : '');

// Nazivi moraju odgovarati cap:areaDesc u feedu. Od 2026-09-22 DHMZ kopno izdaje po županijama;
// morska područja zadržala su stare engleske nazive.
const DHMZ_REGION_GROUPS = [
  { label: 'Županije', options: [
    'Grad Zagreb', 'Zagrebačka', 'Krapinsko-zagorska', 'Varaždinska', 'Međimurska',
    'Koprivničko-križevačka', 'Bjelovarsko-bilogorska', 'Virovitičko-podravska', 'Požeško-slavonska',
    'Brodsko-posavska', 'Osječko-baranjska', 'Vukovarsko-srijemska', 'Karlovačka', 'Sisačko-moslavačka',
    'Primorsko-goranska', 'Ličko-senjska', 'Istarska', 'Zadarska', 'Šibensko-kninska',
    'Splitsko-dalmatinska', 'Dubrovačko-neretvanska',
  ] },
  { label: 'Morska područja', options: [
    'West Istrian coast region', 'Kvarner i Kvarneric region', 'Velebit channel region',
    'North Dalmatia region', 'Middle Dalmatia region', 'South Dalmatia region',
  ] },
];
const DHMZ_REGION_LABELS = {
  'West Istrian coast region': 'Zapadna obala Istre',
  'Kvarner i Kvarneric region': 'Kvarner i Kvarnerić',
  'Velebit channel region': 'Velebitski kanal',
  'North Dalmatia region': 'Sjeverna Dalmacija',
  'Middle Dalmatia region': 'Srednja Dalmacija',
  'South Dalmatia region': 'Južna Dalmacija',
};
const regionLabel = (v) => DHMZ_REGION_LABELS[v] || v;
// Backend: { Yellow: 1, Orange: 2, Red: 3 }[x] || 1 — stara vrijednost 'Minor' = sve razine.
const sevOf = (v) => (v === 'Orange' || v === 'Red' ? v : 'Yellow');
const SEV_OPTIONS = [{ value: 'Yellow', label: 'Žuto' }, { value: 'Orange', label: 'Narančasto' }, { value: 'Red', label: 'Crveno' }];
const SEV_FROM = { Yellow: 'sve razine', Orange: 'od narančastog', Red: 'samo crveno' };

function Switch({ on }) {
  return <span className={`tf-sw${on ? ' on' : ''}`} aria-hidden="true" />;
}
function SwitchRow({ icon, label, sub, on, onChange }) {
  return (
    <button type="button" className="tf-row tf-row-btn" onClick={() => onChange(!on)} aria-pressed={on}>
      {icon && <span className="tf-ico">{icon}</span>}
      <span className="tf-k">{label}{sub && <small>{sub}</small>}</span>
      <Switch on={on} />
    </button>
  );
}
function Stepper({ value, min, max, format, onChange, label }) {
  const n = Number(value);
  return (
    <span className="tf-step">
      <button type="button" aria-label={`${label}: manje`} disabled={n <= min} onClick={() => onChange(String(Math.max(min, n - 1)))}>−</button>
      <b style={{ minWidth: 56 }}>{format ? format(n) : n}</b>
      <button type="button" aria-label={`${label}: više`} disabled={n >= max} onClick={() => onChange(String(Math.min(max, n + 1)))}>+</button>
    </span>
  );
}

// Zajednički panel za uređivanje skupa ključeva: nacrt, Odustani / Spremi, upozorenje pri zatvaranju.
function SettingsSheet({ title, sub, keys, settings, onClose, onSaved, children }) {
  const initial = useMemo(() => Object.fromEntries(keys.map(k => [k, settings[k] ?? defaultOf(k)])), [keys, settings]);
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(null);
  const changed = keys.filter(k => draft[k] !== initial[k]);
  const set = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setError(''); };

  const save = async () => {
    if (!changed.length) return;
    setBusy(true);
    try {
      const updates = Object.fromEntries(changed.map(k => [k, draft[k]]));
      await api.put('/settings', updates);
      onSaved(updates);
    } catch {
      setError('Spremanje nije uspjelo.');
      setBusy(false);
    }
  };

  return (
    <Sheet tall onClose={onClose} dirty={changed.length > 0} busy={busy} closeRef={closeRef}
      head={<div className="adm-sh"><div className="adm-main"><h3>{title}</h3>{sub && <p>{sub}</p>}</div></div>}
      footer={
        <div className="adm-dirty">
          {changed.length > 0 && <p>{changed.length} {plural(changed.length, 'promjena nije spremljena', 'promjene nisu spremljene', 'promjena nije spremljeno')}</p>}
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <div className="tf-foot">
            <button type="button" className="adm-btn" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>Odustani</button>
            <button type="button" className={`adm-btn ${changed.length ? 'primary' : 'tf-off'}`} disabled={!changed.length || busy} onClick={save}>
              {busy ? 'Spremam…' : changed.length ? 'Spremi' : 'Nema promjena'}
            </button>
          </div>
        </div>
      }
    >
      {children(draft, set)}
    </Sheet>
  );
}

function TasksBody(d, set) {
  return <>
    <div className="adm-lbl">Broj ljudi na zadatku (x/y)</div>
    <Seg value={d.task_count_mode} onChange={v => set('task_count_mode', v)}
      options={[{ value: 'assigned', label: 'Dodijeljeni' }, { value: 'accepted', label: 'Prihvatili' }]} />
    <p className="adm-note">Dodijeljeni = pozvani i prihvaćeni, bez odbijenih. Prihvatili = samo oni koji su potvrdili.</p>

    <div className="adm-lbl">Podsjetnici</div>
    <div className="adm-card">
      <SwitchRow icon={<CalendarDays size={17} />} label="Dan(a) prije zadatka" sub="Jednom dnevno u zadani sat"
        on={d.reminder_days_enabled === 'true'} onChange={v => set('reminder_days_enabled', v ? 'true' : 'false')} />
      {d.reminder_days_enabled === 'true' && <>
        <div className="tf-row st-sub"><span className="tf-k">Koliko dana prije</span>
          <Stepper label="Dana prije" value={d.reminder_days} min={1} max={30} format={n => `${n} d`} onChange={v => set('reminder_days', v)} /></div>
        <div className="tf-row st-sub"><span className="tf-k">U koliko sati</span>
          <Stepper label="Sat" value={d.reminder_days_hour} min={0} max={23} format={n => `${String(n).padStart(2, '0')}:00`} onChange={v => set('reminder_days_hour', v)} /></div>
      </>}
      <SwitchRow icon={<Clock size={17} />} label="Sat(i) prije početka" sub="Provjera svakih 15 minuta"
        on={d.reminder_hours_enabled === 'true'} onChange={v => set('reminder_hours_enabled', v ? 'true' : 'false')} />
      {d.reminder_hours_enabled === 'true' && (
        <div className="tf-row st-sub"><span className="tf-k">Koliko sati prije</span>
          <Stepper label="Sati prije" value={d.reminder_hours} min={1} max={48} format={n => `${n} h`} onChange={v => set('reminder_hours', v)} /></div>
      )}
    </div>
  </>;
}

function DhmzBody(d, set) {
  const known = DHMZ_REGION_GROUPS.some(g => g.options.includes(d.dhmz_region));
  const on = d.dhmz_enabled === 'true';
  return <>
    <div className="adm-card">
      <SwitchRow icon={<CloudLightning size={17} />} label="Prati upozorenja" sub="Provjera svakih 15 minuta; prikaz u Aktualno i Obavijestima"
        on={on} onChange={v => set('dhmz_enabled', v ? 'true' : 'false')} />
      {on && (
        <label className="tf-row st-sub">
          <span className="tf-ico"><MapPin size={17} /></span>
          <span className="tf-k">Područje</span>
          <select id="st-region" className="st-select" value={d.dhmz_region} onChange={e => set('dhmz_region', e.target.value)}>
            {!known && <option value={d.dhmz_region}>{d.dhmz_region} (stari naziv — odaberi ponovno)</option>}
            {DHMZ_REGION_GROUPS.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(v => <option key={v} value={v}>{regionLabel(v)}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
      )}
    </div>
    {on && <>
      <div className="adm-lbl">Prikazuj upozorenja od razine</div>
      <Seg value={sevOf(d.dhmz_min_severity)} onChange={v => set('dhmz_min_severity', v)} options={SEV_OPTIONS} />
      <div className="adm-lbl">Push na mobitel</div>
      <div className="adm-card">
        <SwitchRow icon={<Bell size={17} />} label="Šalji push" sub="Isključi ako želiš samo prikaz u aplikaciji"
          on={d.dhmz_push_enabled === 'true'} onChange={v => set('dhmz_push_enabled', v ? 'true' : 'false')} />
      </div>
      {d.dhmz_push_enabled === 'true' && <>
        <div className="adm-lbl">Push od razine</div>
        <Seg value={sevOf(d.dhmz_push_min_severity)} onChange={v => set('dhmz_push_min_severity', v)} options={SEV_OPTIONS} />
      </>}
    </>}
  </>;
}

function PushBody(d, set) {
  return PUSH_GROUPS.map(g => {
    const allOn = g.items.every(([k]) => d[k] === 'true');
    return (
      <React.Fragment key={g.label}>
        <div className="adm-lbl st-lbl-act">{g.label}
          {g.items.length > 1 && (
            <button type="button" onClick={() => g.items.forEach(([k]) => set(k, allOn ? 'false' : 'true'))}>{allOn ? 'isključi sve' : 'uključi sve'}</button>
          )}
        </div>
        <div className="adm-card">
          {g.items.map(([k, label, sub]) => (
            <SwitchRow key={k} label={label} sub={sub} on={d[k] === 'true'} onChange={v => set(k, v ? 'true' : 'false')} />
          ))}
        </div>
      </React.Fragment>
    );
  });
}

function ReportsSheet({ onClose }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = ['Cijela godina', 'Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];
  const [type, setType] = useState('tasks');
  const [f, setF] = useState({ year: currentYear, month: 0, status: 'all', group_id: '', category_id: '', format: 'xlsx' });
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/groups').then(r => setGroups(r.data || [])).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const download = async () => {
    setBusy(true); setError('');
    try {
      const params = new URLSearchParams();
      params.append('year', f.year);
      if (f.month) params.append('month', f.month);
      params.append('format', f.format);
      if (type === 'tasks') {
        if (f.status !== 'all') params.append('status', f.status);
        if (f.group_id) params.append('group_id', f.group_id);
        if (f.category_id) params.append('category_id', f.category_id);
      }
      const res = await fetch(`/api/export/${type === 'tasks' ? 'tasks' : 'interventions'}?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type === 'tasks' ? 'zadaci' : 'intervencije'}-${f.year}${f.month ? `-${String(f.month).padStart(2, '0')}` : ''}.${f.format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Preuzimanje nije uspjelo.');
    } finally {
      setBusy(false);
    }
  };

  const fmtName = { txt: 'TXT', docx: 'Word', xlsx: 'Excel' }[f.format];
  const sel = (id, label, value, onChange, options) => (
    <label className="tf-row" htmlFor={id}>
      <span className="tf-k">{label}</span>
      <select id={id} className="st-select" value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );

  return (
    <Sheet tall onClose={onClose} busy={busy}
      head={<div className="adm-sh"><div className="adm-main"><h3>Izvještaji</h3><p>Preuzmi popis zadataka ili intervencija.</p></div></div>}
      footer={
        <div className="adm-dirty">
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <button type="button" className="adm-btn primary" disabled={busy} onClick={download}>
            <Download size={17} /> {busy ? 'Preuzimam…' : `Preuzmi ${type === 'tasks' ? 'zadatke' : 'intervencije'} ${f.year}. (${fmtName})`}
          </button>
        </div>
      }
    >
      <Seg value={type} onChange={setType} options={[{ value: 'tasks', label: 'Zadaci' }, { value: 'interventions', label: 'Intervencije' }]} />
      <div className="adm-lbl">Razdoblje i filteri</div>
      <div className="adm-card">
        {sel('rp-year', 'Godina', f.year, v => setF(p => ({ ...p, year: Number(v) })), years.map(y => ({ value: y, label: `${y}.` })))}
        {sel('rp-month', 'Mjesec', f.month, v => setF(p => ({ ...p, month: Number(v) })), months.map((m, i) => ({ value: i, label: m })))}
        {type === 'tasks' && <>
          {sel('rp-status', 'Status', f.status, v => setF(p => ({ ...p, status: v })), [{ value: 'all', label: 'Svi' }, { value: 'active', label: 'Aktivni' }, { value: 'done', label: 'Završeni' }])}
          {groups.length > 0 && sel('rp-group', 'Grupa', f.group_id, v => setF(p => ({ ...p, group_id: v })), [{ value: '', label: 'Sve' }, ...groups.map(g => ({ value: String(g.id), label: g.name }))])}
          {categories.length > 0 && sel('rp-cat', 'Kategorija', f.category_id, v => setF(p => ({ ...p, category_id: v })), [{ value: '', label: 'Sve' }, ...categories.map(c => ({ value: String(c.id), label: `${c.emoji || ''} ${c.name}`.trim() }))])}
        </>}
      </div>
      <div className="adm-lbl">Format</div>
      <Seg value={f.format} onChange={v => setF(p => ({ ...p, format: v }))}
        options={[{ value: 'txt', label: 'TXT' }, { value: 'docx', label: 'Word' }, { value: 'xlsx', label: 'Excel' }]} />
    </Sheet>
  );
}

function Settings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);   // 'tasks' | 'dhmz' | 'push' | 'reports'
  const [toast, setToast] = useState('');

  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data || {})).catch(() => setError('Greška pri dohvatu postavki.'));
  }, []);

  if (error) return <div className="st-page"><p className="adm-err">{error}</p></div>;
  if (!settings) return <div className="loading">Učitavanje...</div>;

  const v = (k) => settings[k] ?? defaultOf(k);
  const onSaved = (updates) => {
    setSettings(s => ({ ...s, ...updates }));
    setOpen(null);
    setToast('Postavke su spremljene.');
    setTimeout(() => setToast(''), 2500);
  };

  // Sažeci za izbornik
  const reminders = [
    v('reminder_days_enabled') === 'true' && `${v('reminder_days')} ${plural(Number(v('reminder_days')), 'dan', 'dana', 'dana')} prije u ${String(v('reminder_days_hour')).padStart(2, '0')}:00`,
    v('reminder_hours_enabled') === 'true' && `${v('reminder_hours')} h prije`,
  ].filter(Boolean);
  const tasksSummary = `Brojimo ${v('task_count_mode') === 'accepted' ? 'prihvaćene' : 'dodijeljene'} · ${reminders.length ? `podsjetnik ${reminders.join(' i ')}` : 'bez podsjetnika'}`;
  const dhmzOn = v('dhmz_enabled') === 'true';
  const dhmzSummary = dhmzOn
    ? `${regionLabel(v('dhmz_region'))} · ${v('dhmz_push_enabled') === 'true' ? `push ${SEV_FROM[sevOf(v('dhmz_push_min_severity'))]}` : 'bez pusha'}`
    : 'Ne prati se';
  const offPush = PUSH_GROUPS.flatMap(g => g.items).filter(([k]) => v(k) !== 'true');
  const pushSummary = offPush.length === 0
    ? `Svih ${PUSH_KEYS.length} vrsta uključeno`
    : `${PUSH_KEYS.length - offPush.length} od ${PUSH_KEYS.length} uključeno · isključeno: ${offPush.slice(0, 2).map(i => i[1]).join(', ')}${offPush.length > 2 ? ` i još ${offPush.length - 2}` : ''}`;

  const row = (key, tile, Icon, title, summary, badge) => (
    <button type="button" className="st-row" onClick={() => setOpen(key)}>
      <span className="st-tile" style={{ background: tile[0], color: tile[1] }}><Icon size={18} /></span>
      <span className="st-k">{title}<small>{summary}</small></span>
      {badge || <ChevronRight size={17} color="#b2bec3" />}
    </button>
  );

  return (
    <div className="st-page">
      <h2 className="st-h">Postavke</h2>
      <p className="st-intro">Vrijede za cijeli DVD. Vidi ih samo admin.</p>

      <div className="adm-lbl">Rad</div>
      <div className="adm-card">
        {row('tasks', ['#e7eff7', '#3a6ea3'], ClipboardList, 'Zadaci', tasksSummary)}
        {row('dhmz', ['#f6efe1', '#a8781f'], CloudLightning, 'Vremenska upozorenja', dhmzSummary,
          <span className={`st-badge ${dhmzOn ? 'on' : 'off'}`}>{dhmzOn ? 'uključeno' : 'isključeno'}</span>)}
      </div>

      <div className="adm-lbl">Obavijesti</div>
      <div className="adm-card">
        {row('push', ['#e8ebf1', '#1a2a4a'], Bell, 'Push obavijesti', pushSummary)}
      </div>

      <div className="adm-lbl">Podaci</div>
      <div className="adm-card">
        {row('reports', ['#e6f2ec', '#3f8f6b'], Download, 'Izvještaji', 'Preuzmi zadatke ili intervencije (TXT, Word, Excel)')}
      </div>

      {open === 'tasks' && (
        <SettingsSheet title="Zadaci" keys={['task_count_mode', 'reminder_days_enabled', 'reminder_days', 'reminder_days_hour', 'reminder_hours_enabled', 'reminder_hours']}
          settings={settings} onClose={() => setOpen(null)} onSaved={onSaved}>{TasksBody}</SettingsSheet>
      )}
      {open === 'dhmz' && (
        <SettingsSheet title="Vremenska upozorenja" sub="DHMZ upozorenja za odabrano područje"
          keys={['dhmz_enabled', 'dhmz_region', 'dhmz_min_severity', 'dhmz_push_enabled', 'dhmz_push_min_severity']}
          settings={settings} onClose={() => setOpen(null)} onSaved={onSaved}>{DhmzBody}</SettingsSheet>
      )}
      {open === 'push' && (
        <SettingsSheet title="Push obavijesti" sub="Koje vrste se šalju na mobitel. U Obavijestima se vide uvijek."
          keys={PUSH_KEYS} settings={settings} onClose={() => setOpen(null)} onSaved={onSaved}>{PushBody}</SettingsSheet>
      )}
      {open === 'reports' && <ReportsSheet onClose={() => setOpen(null)} />}
      {toast && <div className="pf-toast" role="status">{toast}</div>}
    </div>
  );
}

export default Settings;
